import { useEffect, useState, type ReactNode } from 'react';
import { currencySettingsSchema } from '@forecastify/shared';
import { Button, Dialog } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { dealMoney } from './deal-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D6 · what the Terv and Erőforrás tabs share.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 */

/**
 * A typed amount, or null when it is not one. Spaces are thousands separators and a comma is the
 * decimal point — `1 080 000`, `4200,50` — the same reading the contract's `moneyInput` gives it.
 */
export function parseAmount(text: string): number | null {
  const trimmed = text.replace(/\s/gu, '').replace(',', '.');
  if (trimmed === '') return 0;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) / 100 : null;
}

/** A wire amount as the field shows it: `"4000000.00"` → `4000000`. */
export function amountText(value: string | null): string {
  if (value === null) return '';
  const n = Number(value);
  return Number.isFinite(n) && n !== 0 ? String(n) : '';
}

/**
 * ⚠️ **Money is summed PER CURRENCY** — two line items in two currencies have no total without a
 * rate, and converting here would be a figure the server never agreed to. `4 000 000 Ft + € 1 200`.
 */
export function moneyByCurrency(
  rows: readonly { readonly amount: string; readonly currencyCode: string }[],
  fallbackCurrency: string,
): string {
  const sums = new Map<string, number>();
  for (const row of rows) {
    sums.set(row.currencyCode, (sums.get(row.currencyCode) ?? 0) + Number(row.amount));
  }
  if (sums.size === 0) return dealMoney(0, fallbackCurrency);
  return [...sums.entries()].map(([code, sum]) => dealMoney(sum, code)).join(' + ');
}

/**
 * The workspace's active currencies, for the dialogs' pickers. ⚠️ Whatever the record already
 * names is always offered too — a line item in a currency that was since switched off must still
 * open with its own value selected rather than silently changing on save.
 */
export function useActiveCurrencies(always: readonly string[]): readonly string[] {
  const session = useSession();
  const [codes, setCodes] = useState<readonly string[]>([]);

  useEffect(() => {
    if (session.workspaceId === null) return;
    /* An answer arriving after unmount is dropped, not set. */
    const state = { live: true };
    void (async () => {
      const response = await session.request('/workspace/currencies').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = currencySettingsSchema.safeParse(await response.json());
      if (parsed.success && state.live) {
        setCodes(parsed.data.currencies.filter((one) => one.active).map((one) => one.code));
      }
    })();
    return () => {
      state.live = false;
    };
  }, [session]);

  return [...new Set([...always.filter((code) => code !== ''), ...codes])];
}

/**
 * A yes/no before a delete that takes other rows with it. The sentence names what goes — a
 * line item's hours, a placeholder's every week — because *"Biztos?"* answers nothing.
 */
export function PlanConfirm({
  open,
  title,
  body,
  confirmLabel,
  error,
  onCancel,
  onConfirm,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const t = useT();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title={title}
      description={body}
      closeLabel={t('common.close')}
      width={440}
    >
      <div className="flex flex-col gap-3">
        {error === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error}
          </div>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="lg" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" size="lg" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** A section heading with its one action — the Terv tab's three blocks share it. */
export function PlanSectionHead({
  title,
  hint,
  action,
}: {
  readonly title: string;
  readonly hint?: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="flex items-end gap-3 px-4 pt-5 pb-2">
      <div className="min-w-0 flex-1">
        <h2 className="text-[13px] font-medium">{title}</h2>
        {hint === undefined ? null : (
          <p className="text-muted-foreground mt-0.5 text-[12px]">{hint}</p>
        )}
      </div>
      {action}
    </div>
  );
}
